import React from 'react';
import { Alert, Platform, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import * as Google from 'expo-auth-session/providers/google';
import * as WebBrowser from 'expo-web-browser';
import * as AppleAuthentication from 'expo-apple-authentication';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';
import ToastAlert from '../helpers/ToastAlert';
import { WEBVIEW_PROVIDERS, getClientId } from '../utils/socialOAuth';
import { runWebOAuthPopup } from '../utils/socialOAuthWeb';
import { GOOGLE_NATIVE_CLIENT_IDS } from '../config/socialConfig';
import { isFeatureEnabled } from '../config/featureFlags';
import { socialLogin } from '../services/socialAuthApi';
import SocialLoginWebView from './SocialLoginWebView';
import {
    authenticateBiometric,
    getBiometricKind,
    getBiometricSession,
    isBiometricAvailable,
    isBiometricLoginEnabled,
} from '../helpers/biometricAuth';

const IS_WEB = Platform.OS === 'web';

// Finish any pending auth-session redirect when the app is re-focused. Guarded:
// `maybeCompleteAuthSession` reaches the native ExpoWebBrowser module, which can
// be undefined in a partially-linked release archive (autolinking perturbed when
// the VoIP/widget native modules were added). A throw here must never abort the
// module load and take the whole login screen down with it.
try {
    WebBrowser.maybeCompleteAuthSession();
} catch (_error) {
    // Native web-browser module unavailable in this binary — social redirect
    // completion is a no-op; email/password + the guarded buttons still work.
}

// WebView-friendly providers (Google uses the system browser; Apple the native sheet).
// `icon` is a MaterialCommunityIcons brand glyph (font already bundled app-wide).
const PROVIDERS = [
    { key: 'microsoft', label: 'Microsoft', color: '#0067B8', icon: 'microsoft' },
    { key: 'facebook', label: 'Facebook', color: '#1877F2', icon: 'facebook' },
    { key: 'github', label: 'GitHub', color: '#24292F', icon: 'github' },
    { key: 'linkedin', label: 'LinkedIn', color: '#0A66C2', icon: 'linkedin' },
];

// The Google OAuth client id that `expo-auth-session` actually requires on THIS
// platform: iOS → iosClientId, Android → androidClientId, web → webClientId. The
// provider's `invariantClientId` throws synchronously during render if that exact
// id is missing (webClientId does NOT satisfy the native platforms). We must
// therefore never mount the Google hook unless the current platform's id is set.
const GOOGLE_PLATFORM_CLIENT_ID = Platform.select({
    ios: GOOGLE_NATIVE_CLIENT_IDS.ios,
    android: GOOGLE_NATIVE_CLIENT_IDS.android,
    default: GOOGLE_NATIVE_CLIENT_IDS.web,
});

/**
 * Google sign-in button, isolated so its `useAuthRequest` hook is only ever
 * called when the current platform has a valid Google client id. Mounting is
 * gated by the parent (`GOOGLE_PLATFORM_CLIENT_ID`), so the provider's
 * `invariantClientId` (which throws on a missing native client id) can never
 * fire and crash the login screen.
 *
 * @param {object} props
 * @param {(provider:string, payload:object)=>void} props.onCredential
 * @param {(key:string, opts?:object)=>string} props.t
 */
function GoogleSignInButton({ onCredential, t }) {
    const [request, response, promptAsync] = Google.useAuthRequest({
        iosClientId: GOOGLE_NATIVE_CLIENT_IDS.ios || undefined,
        androidClientId: GOOGLE_NATIVE_CLIENT_IDS.android || undefined,
        webClientId: GOOGLE_NATIVE_CLIENT_IDS.web || undefined,
        scopes: ['openid', 'profile', 'email'],
    });

    React.useEffect(() => {
        if (!response) {
            return;
        }
        if (response.type === 'success') {
            const idToken = response.params?.id_token || response.authentication?.idToken;
            const accessToken = response.authentication?.accessToken;

            if (idToken) {
                onCredential('google', { credential: idToken });
            } else if (accessToken) {
                onCredential('google', { accessToken });
            } else {
                ToastAlert('error', 'top', t('social_failed', { defaultValue: 'Social sign-in failed.' }), '');
            }
        } else if (response.type === 'error') {
            ToastAlert('error', 'top', t('social_failed', { defaultValue: 'Social sign-in failed.' }), response.error?.message || '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [response]);

    return (
        <TouchableOpacity
            style={[styles.iconBtn, { backgroundColor: '#4285F4' }]}
            activeOpacity={0.85}
            disabled={!request}
            onPress={() => promptAsync()}
            accessibilityRole="button"
            accessibilityLabel="Google"
        >
            <MaterialCommunityIcons name="google" size={22} color="#ffffff" />
        </TouchableOpacity>
    );
}

/**
 * Social sign-in block for the mobile login screen:
 *   - Face ID / Touch ID quick re-login (when a session is stored),
 *   - Google (system browser via expo-auth-session),
 *   - Apple (native sheet w/ Face ID, via expo-apple-authentication, iOS),
 *   - Microsoft / Facebook / GitHub / LinkedIn (WebView code flow).
 * On success it calls `onAuthenticated({ user, token })`; a 2FA challenge goes to `on2FA(data)`.
 *
 * @param {object} props
 * @param {(data:object)=>void} props.onAuthenticated
 * @param {(data:object)=>void} [props.on2FA]
 * @param {(loading:boolean)=>void} [props.setLoading]
 * @param {(key:string, opts?:object)=>string} props.t
 */
export default function SocialLoginButtons({ onAuthenticated, on2FA, setLoading, t }) {
    const [activeProvider, setActiveProvider] = React.useState(null);
    const [biometric, setBiometric] = React.useState({ ready: false, kind: 'biometric' });
    const [appleAvailable, setAppleAvailable] = React.useState(false);

    React.useEffect(() => {
        let active = true;

        (async () => {
            const [available, enabled] = await Promise.all([isBiometricAvailable(), isBiometricLoginEnabled()]);
            const kind = available ? await getBiometricKind() : 'biometric';
            let apple = false;

            if (Platform.OS === 'ios') {
                apple = await AppleAuthentication.isAvailableAsync().catch(() => false);
            }
            if (active) {
                setBiometric({ ready: available && enabled, kind });
                setAppleAvailable(apple);
            }
        })();

        return () => { active = false; };
    }, []);

    const submitPayload = async (provider, payload) => {
        setLoading?.(true);
        try {
            const { status, data } = await socialLogin(provider, payload);

            if (status === 200 && data?.requires2FA && data?.preAuthToken) {
                on2FA?.(data);

                return;
            }
            if (status === 200 && data?.token && data?.user?._id) {
                await onAuthenticated?.(data);

                return;
            }
            // Any non-success response reaches here (socialAuthApi never throws on
            // 4xx/5xx). Feedback must use `Alert.alert` — the app shims it to the
            // Modal-based HappyAlert (App.jsx), which stacks ABOVE the full-screen
            // loading spinner; a ToastAlert would render beneath it and be invisible.
            // Drop the spinner first so the user is not left on a frozen overlay.
            setLoading?.(false);

            // 404 USER_NOT_FOUND — the Google/Apple identity is valid but no user
            // exists with that login.
            if (data?.code === 'USER_NOT_FOUND') {
                Alert.alert(
                    t('social_no_user', { defaultValue: 'No valid user with this login' }),
                    t('social_no_user_hint', { defaultValue: 'There is no valid user with this sign-in provider. Contact us if you think your details are right.' }),
                );

                return;
            }
            // 403 — account disabled or system usage rights revoked (ACCESS_REVOKED),
            // or 424 trial expired. The identity is valid but this user may not sign in.
            if (status === 403 || status === 424 || data?.code === 'ACCESS_REVOKED') {
                Alert.alert(
                    t('social_permission_denied', { defaultValue: 'Access denied' }),
                    data?.message || t('social_permission_denied_hint', { defaultValue: 'You do not have permission to sign in. Please contact support.' }),
                );

                return;
            }
            // 401 — invalid / untrusted credential (TOKEN_INVALID, ISSUER_UNTRUSTED,
            // NO_PROVIDER_ID) or any other bad-auth response.
            if (status === 401) {
                Alert.alert(
                    t('social_invalid_login', { defaultValue: 'Invalid login' }),
                    data?.message || t('social_invalid_login_hint', { defaultValue: 'We could not verify your sign-in. Please try again.' }),
                );

                return;
            }
            // Anything else (provider config 400, provider unreachable 502, 500, …).
            Alert.alert(
                t('social_failed', { defaultValue: 'Social sign-in failed.' }),
                data?.message || '',
            );
        } catch (e) {
            setLoading?.(false);
            Alert.alert(
                t('social_failed', { defaultValue: 'Social sign-in failed.' }),
                e?.message || t('social_network_error', { defaultValue: 'Network error. Please check your connection and try again.' }),
            );

            return;
        } finally {
            setLoading?.(false);
        }
    };

    // The build-time switchboard (config/featureFlags.js) is checked FIRST: an
    // App Store review build ships with `auth.social` off, so no provider is
    // even offered and only e-mail + password remains.
    const providers = PROVIDERS
        .filter((p) => WEBVIEW_PROVIDERS.includes(p.key))
        .filter((p) => isFeatureEnabled(`auth.social.${p.key}`));
    // Only true when THIS platform's Google client id is present — the sole
    // condition under which mounting the Google hook is safe (see above).
    const googleConfigured = !!GOOGLE_PLATFORM_CLIENT_ID && isFeatureEnabled('auth.social.google');
    // Apple joins the icon strip when the native sheet is available (iOS) or, on
    // Android, when the web authorization-code flow is configured.
    const appleInRow = isFeatureEnabled('auth.social.apple')
        && (appleAvailable || (Platform.OS === 'android' && !!getClientId('apple')));
    // Biometric unlock is a separate switch — it reuses a session the user
    // already created with e-mail + password, so it can stay on in a build that
    // has social sign-in turned off.
    const biometricEnabled = biometric.ready && isFeatureEnabled('auth.biometric');
    const anySocial = appleInRow || googleConfigured || providers.length > 0;

    const handleWebResult = (result) => {
        setActiveProvider(null);

        if (!result || result.error) {
            if (result?.error && result.error !== 'cancelled') {
                ToastAlert('error', 'top', t('social_failed', { defaultValue: 'Social sign-in failed.' }), '');
            }

            return;
        }
        if (result.code) {
            submitPayload(result.provider, { code: result.code, redirectUri: result.redirectUri });
        }
    };

    const startProvider = (provider) => {
        if (!getClientId(provider)) {
            ToastAlert('info', 'top', t('social_not_configured', { defaultValue: "This sign-in method isn't configured yet." }), '');

            return;
        }
        // On web there is no usable `react-native-webview`; run the same popup
        // flow the admin panel uses (window.open → backend relay → postMessage).
        if (IS_WEB) {
            startWebProvider(provider);

            return;
        }
        setActiveProvider(provider);
    };

    /**
     * Web-only provider sign-in via popup. `runWebOAuthPopup` opens the popup
     * synchronously (kept inside this tap) and resolves the credential returned
     * by the backend relay, which is then exchanged through `submitPayload`.
     * @param {string} provider
     */
    const startWebProvider = (provider) => {
        // Open the popup FIRST (still synchronous with the tap) so the browser
        // doesn't block it; only then flip the overlay + await the result.
        const pending = runWebOAuthPopup(provider);

        setLoading?.(true);
        pending
            .then((result) => {
                const payload = result.code
                    ? { code: result.code, redirectUri: result.redirectUri }
                    : result.idToken
                        ? { credential: result.idToken }
                        : result.accessToken
                            ? { accessToken: result.accessToken }
                            : null;

                if (!payload) {
                    setLoading?.(false);
                    ToastAlert('error', 'top', t('social_failed', { defaultValue: 'Social sign-in failed.' }), '');

                    return undefined;
                }

                return submitPayload(provider, payload);
            })
            .catch((e) => {
                setLoading?.(false);
                const reason = e?.message;

                if (reason === 'cancelled') {
                    return; // user closed the popup — silent
                }
                if (reason === 'popup_blocked') {
                    Alert.alert(
                        t('social_popup_blocked', { defaultValue: 'Popup blocked' }),
                        t('social_popup_blocked_hint', { defaultValue: 'Please allow popups for this site, then try signing in again.' }),
                    );

                    return;
                }
                ToastAlert('error', 'top', t('social_failed', { defaultValue: 'Social sign-in failed.' }), '');
            });
    };

    const startApple = async () => {
        try {
            const credential = await AppleAuthentication.signInAsync({
                requestedScopes: [
                    AppleAuthentication.AppleAuthenticationScope.FULL_NAME,
                    AppleAuthentication.AppleAuthenticationScope.EMAIL,
                ],
            });

            if (credential?.identityToken) {
                submitPayload('apple', {
                    credential: credential.identityToken,
                    firstName: credential.fullName?.givenName || undefined,
                    lastName: credential.fullName?.familyName || undefined,
                });
            }
        } catch (e) {
            if (e?.code !== 'ERR_REQUEST_CANCELED') {
                ToastAlert('error', 'top', t('social_failed', { defaultValue: 'Social sign-in failed.' }), e?.message || '');
            }
        }
    };

    const biometricLogin = async () => {
        const kindLabel = biometric.kind === 'faceId'
            ? t('biometric_faceid', { defaultValue: 'Face ID' })
            : t('biometric_touchid', { defaultValue: 'Touch ID' });
        const ok = await authenticateBiometric(t('biometric_prompt', { defaultValue: `Sign in with ${kindLabel}` }));

        if (!ok) {
            return;
        }
        const session = await getBiometricSession();

        if (session?.token && session?.user?._id) {
            await onAuthenticated?.(session);
        } else {
            ToastAlert('info', 'top', t('biometric_none', { defaultValue: 'No saved session for biometric login.' }), '');
        }
    };

    // Nothing left to offer (e.g. the review build: social off, no saved
    // biometric session) — render nothing at all rather than an empty
    // "or continue with" divider hanging under the login form.
    if (!biometricEnabled && !anySocial) {
        return null;
    }

    return (
        <View style={styles.wrap}>
            {biometricEnabled && (
                <TouchableOpacity style={styles.bioBtn} activeOpacity={0.85} onPress={biometricLogin}>
                    <Text style={styles.bioText}>
                        {biometric.kind === 'faceId'
                            ? t('biometric_login_faceid', { defaultValue: 'Sign in with Face ID' })
                            : t('biometric_login_touchid', { defaultValue: 'Sign in with Touch ID' })}
                    </Text>
                </TouchableOpacity>
            )}

            {anySocial && (
                <View style={styles.divider}>
                    <View style={styles.line} />
                    <Text style={styles.dividerText}>{t('social_or_continue', { defaultValue: 'or continue with' })}</Text>
                    <View style={styles.line} />
                </View>
            )}

            {/* One unified strip of brand-icon buttons: Apple + Google (native flows)
                alongside the WebView providers. Apple uses the native sheet on iOS
                and the web authorization-code flow on Android. */}
            <View style={styles.row}>
                {appleInRow && (
                    <TouchableOpacity
                        style={[styles.iconBtn, { backgroundColor: '#000000' }]}
                        activeOpacity={0.85}
                        onPress={appleAvailable ? startApple : () => startProvider('apple')}
                        accessibilityRole="button"
                        accessibilityLabel="Apple"
                    >
                        <MaterialCommunityIcons name="apple" size={23} color="#ffffff" />
                    </TouchableOpacity>
                )}

                {googleConfigured && <GoogleSignInButton onCredential={submitPayload} t={t} />}

                {providers.map((p) => (
                    <TouchableOpacity
                        key={p.key}
                        style={[styles.iconBtn, { backgroundColor: p.color }]}
                        activeOpacity={0.85}
                        onPress={() => startProvider(p.key)}
                        accessibilityRole="button"
                        accessibilityLabel={p.label}
                    >
                        <MaterialCommunityIcons name={p.icon} size={22} color="#ffffff" />
                    </TouchableOpacity>
                ))}
            </View>

            {/* Native only — on web the provider row uses the window.open popup
                flow (startWebProvider); the WebView is never mounted there.
                Skipped entirely when the build has no social providers. */}
            {!IS_WEB && anySocial && (
                <SocialLoginWebView
                    provider={activeProvider}
                    visible={!!activeProvider}
                    t={t}
                    onClose={() => setActiveProvider(null)}
                    onResult={handleWebResult}
                />
            )}
        </View>
    );
}

const styles = StyleSheet.create({
    wrap: { width: '100%', marginTop: 16 },
    bioBtn: {
        borderWidth: 1.5,
        borderColor: '#f6821f',
        borderRadius: 14,
        paddingVertical: 12,
        alignItems: 'center',
        marginBottom: 10,
    },
    bioText: { color: '#f6821f', fontSize: 15, fontWeight: '700' },
    divider: { flexDirection: 'row', alignItems: 'center', marginVertical: 14 },
    line: { flex: 1, height: StyleSheet.hairlineWidth, backgroundColor: '#CBD5E1' },
    dividerText: { marginHorizontal: 10, color: '#94A3B8', fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.6 },
    // Reflows to a centered second line on narrow phones (iPhone SE / small
    // Android / display-zoom) so six 46px icons never overlap or clip; on normal
    // widths they sit on one evenly-spaced row.
    row: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-evenly', alignItems: 'center', rowGap: 12, columnGap: 12, marginTop: 2 },
    iconBtn: {
        width: 46,
        height: 46,
        borderRadius: 23,
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#0F172A',
        shadowOpacity: 0.16,
        shadowRadius: 7,
        shadowOffset: { width: 0, height: 3 },
        elevation: 4,
    },
});
